import { Card, CardContent, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AppHeader from "@saleor/components/AppHeader";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import {
  InstagramDetails_instagram,
  InstagramDetails_instagram_instagramtag,
  InstagramDetails_instagram_products
} from "@saleor/instagram/types/InstagramDetails";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchProductVariants_search_edges_node } from "@saleor/searches/types/SearchProductVariants";
import { FetchMoreProps, ListActions, PageListProps } from "@saleor/types";
import moment from "moment-timezone";
import React from "react";
import { useIntl } from "react-intl";

import InstagramProducts from "../InstagramProducts";
import InstagramTagListPage from "../InstagramTagListPage";

export interface InstagramDetailsPageProps extends PageListProps, ListActions {
  instagram: InstagramDetails_instagram;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  addInsTag: () => void;
  deleteInsTag: (id: string) => void;
  onSubmit: (data: InstagramDetailsPageFormData) => void;
  onBack: () => void;
  onProductUnassign: (id: string, event: React.MouseEvent<any>) => void;
  fetchMoreVariants: FetchMoreProps;
  fetchVariants: (data: string) => void;
  toolbarTag: React.ReactNode;
  variants: SearchProductVariants_search_edges_node[];
  onRowTagClick: (id: string) => () => void;
  reverseVisible?: (data: InstagramDetails_instagram_products[]) => void;
}

const useStyles = makeStyles(
  theme => ({
    image: {
      height: "500px",
      // objectFit: "contain",
      // userSelect: "none",
      width: "100%"
    },
    listImage: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      marginRight: 30
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      padding: theme.spacing(2),
      position: "relative",
      width: 140
    },
    imageListContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      padding: theme.spacing(2),
      position: "relative",
      width: 560,
      textAlign: "left"
    },
    root: {
      display: "flex",
      justifyContent: "center",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(2),
      gridTemplateColumns: "repeat(4, 1fr)",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(3, 1fr)"
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, 1fr)"
      }
    }
  }),
  { name: "ProductImages" }
);

export interface InstagramDetailsPageFormData {
  published: boolean;
  image: string;
  startTime: number;
  intervalTime: number;
  instagramId: string;
  username: string;
  url: string;
  likes: number;
  number: number;
  createTime: string;
  products: InstagramDetails_instagram_products[];
  instagramTag: InstagramDetails_instagram_instagramtag[];
  shortcode: string;
}

const InstagramDetailsPage: React.FC<InstagramDetailsPageProps> = ({
  instagram,
  onSubmit,
  disabled,
  saveButtonBarState,
  variants: variantChoiceList,
  onBack,
  addInsTag,
  toolbarTag,
  fetchMoreVariants,
  fetchVariants,
  deleteInsTag,
  onRowTagClick,
  reverseVisible,
  ...InstagramDetailsPageProps
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const initialForm: InstagramDetailsPageFormData =
    instagram === null
      ? {
          published: false,
          instagramId: "",
          image: "",
          username: "",
          url: "",
          createTime: "",
          likes: 0,
          number: 0,
          products: [],
          instagramTag: [],
          shortcode: "",
          startTime: 3,
          intervalTime: 5
        }
      : {
          published: maybe(() => instagram.published, false),
          image: maybe(() => instagram.image, ""),
          instagramId: maybe(() => instagram.instagramId, ""),
          username: maybe(() => instagram.username, ""),
          url: maybe(() => instagram.url, ""),
          likes: maybe(() => instagram.likes, 0),
          number: maybe(() => instagram.number, 0),
          createTime: maybe(() => instagram.createTime, ""),
          products: maybe(() => instagram.products, []),
          instagramTag: maybe(() => instagram.instagramtag, []),
          shortcode: maybe(() => instagram.shortcode, ""),
          startTime: maybe(() => instagram.startTime, 3),
          intervalTime: maybe(() => instagram.intervalTime, 5)
        };
  const handleSubmit = (data: InstagramDetailsPageFormData) =>
    onSubmit({
      ...data
    });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.instagramList)}
          </AppHeader>
          <PageHeader
            title={intl.formatMessage({
              defaultMessage: "Content Details",
              description: "Content Details"
            })}
          />
          <Grid>
            <Card>
              <CardTitle
                title={intl.formatMessage(commonMessages.generalInformations)}
              ></CardTitle>
              <CardContent>
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "Content Id",
                    description: "Content Id"
                  })}
                  name={"instagramId" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.instagramId}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "Short Code",
                    description: "Short Code"
                  })}
                  name={"shortcode" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.shortcode}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "User Name",
                    description: "User Name"
                  })}
                  name={"username" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.username}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "Url",
                    description: "Url"
                  })}
                  name={"url" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.url}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "Likes",
                    description: "likes"
                  })}
                  name={"likes" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.likes}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "Comments",
                    description: "Comments"
                  })}
                  name={"number" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.number}
                  onChange={change}
                />
                <FormSpacer />
                <FormSpacer />
                <TextField
                  disabled
                  label={intl.formatMessage({
                    defaultMessage: "Create Time",
                    description: "createTime"
                  })}
                  name={"createTime" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={moment(data.createTime).format("YYYY-MM-DD HH:mm:ss")}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled={disabled}
                  label={intl.formatMessage({
                    defaultMessage: "The first product appeared(s)",
                    description: "The first product appeared"
                  })}
                  name={"startTime" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.startTime}
                  onChange={change}
                />
                <FormSpacer />
                <TextField
                  disabled={disabled}
                  label={intl.formatMessage({
                    defaultMessage: "The time between each product display(s)",
                    description: "The time between each product display"
                  })}
                  name={"intervalTime" as keyof InstagramDetailsPageFormData}
                  fullWidth
                  value={data.intervalTime}
                  onChange={change}
                />
                <FormSpacer />
                <ControlledCheckbox
                  name={"published" as keyof InstagramDetailsPageFormData}
                  label={intl.formatMessage({
                    defaultMessage: "Published",
                    description: "published"
                  })}
                  checked={data.published}
                  onChange={change}
                  disabled={disabled}
                />
              </CardContent>
            </Card>
            <FormSpacer />
            <Card>
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Image",
                  description: "Image"
                })}
              ></CardTitle>
              <CardContent>
                <div className={classes.root}>
                  <div>
                    {instagram?.mimeType === "image/jpeg" && (
                      <img className={classes.image} src={data?.image} />
                    )}
                    {instagram?.mimeType === "video/mp4" && (
                      <video controls={true} className={classes.image}>
                        <source
                          className={classes.image}
                          src={data?.image}
                        ></source>
                      </video>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <FormSpacer />
            <InstagramProducts
              products={data?.products}
              disabled={disabled}
              reverseVisible={reverseVisible}
              {...InstagramDetailsPageProps}
            />
            <FormSpacer />
            <InstagramTagListPage
              image={data.image}
              instagramTag={data?.instagramTag}
              mimeType={instagram?.mimeType}
              disabled={disabled}
              toolbar={toolbarTag}
              onAdd={addInsTag}
              onNextPage={null}
              onPreviousPage={null}
              onRowClick={onRowTagClick}
              toggle={InstagramDetailsPageProps.toggle}
              toggleAll={InstagramDetailsPageProps.toggleAll}
              isChecked={InstagramDetailsPageProps.isChecked}
              selected={InstagramDetailsPageProps.selected}
              // deleteInsTag={deleteInsTag}
            />
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            state={saveButtonBarState}
            onCancel={onBack}
            onSave={submit}
          />
        </Container>
      )}
    </Form>
  );
};

export default InstagramDetailsPage;
